import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useClientPreferences } from '../hooks/Preferences';
import { useClientPreferencesData } from './ClientPreferencesContext';

export type Language = 'fr' | 'en' | 'es' | 'de' | 'lb' | 'nl';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => Promise<void>;
  t: (key: string, params?: Record<string, string | number>) => string;
  isLanguageLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translationsCache: Record<Language, Record<string, unknown>> = {
  fr: {},
  en: {},
  es: {},
  de: {},
  lb: {},
  nl: {},
};

const loadTranslations = async (lang: Language): Promise<Record<string, unknown>> => {
  if (Object.keys(translationsCache[lang]).length > 0) {
    return translationsCache[lang];
  }

  try {
    const translations = await import(`../translations/${lang}.json`);
    translationsCache[lang] = translations.default;
    return translations.default;
  } catch (error) {
    console.error(`Failed to load translations for ${lang}:`, error);
    return {};
  }
};

const getNestedValue = (obj: Record<string, unknown>, path: string): string | undefined => {
  return path.split('.').reduce((current: any, key) => current?.[key], obj);
};

const replaceParams = (str: string, params?: Record<string, string | number>): string => {
  if (!params) return str;
  return Object.entries(params).reduce((result, [key, value]) => 
    result.replace(new RegExp(`{{${key}}}`, 'g'), String(value)), str
  );
};

interface LanguageProviderProps {
  children: ReactNode;
  defaultLanguage?: Language;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ 
  children, 
  defaultLanguage = 'fr' 
}) => {
  const { getClientLanguage, updateClientLanguage } = useClientPreferences();
  const { enseigneCode, clientId, isLoading: isClientLoading } = useClientPreferencesData();
  
  const [language, setLanguageState] = useState<Language>(defaultLanguage);
  const [isReady, setIsReady] = useState(false);
  const [isLanguageLoading, setIsLanguageLoading] = useState(false);
  const hasLoadedFromAPIRef = useRef(false);
  const isLoadingRef = useRef(false);
  const lastLoadTimestampRef = useRef<number>(0);

  // Précharger toutes les traductions
  useEffect(() => {
    const preloadAllLanguages = async () => {
      const languages: Language[] = ['fr', 'en', 'es', 'de', 'lb', 'nl'];
      await Promise.all(languages.map(lang => loadTranslations(lang)));
      setIsReady(true);
    };
    preloadAllLanguages();
  }, []);

  // Synchroniser la langue depuis l'API après l'authentification
  useEffect(() => {
    const syncLanguage = async () => {
      const now = Date.now();
      const timeSinceLastLoad = now - lastLoadTimestampRef.current;
      
      if (!enseigneCode || !clientId || isClientLoading || hasLoadedFromAPIRef.current || isLoadingRef.current || timeSinceLastLoad < 500) {
        return;
      }
      
      lastLoadTimestampRef.current = now;
      isLoadingRef.current = true;
      setIsLanguageLoading(true);
      try {
        const apiLanguage = await getClientLanguage(enseigneCode, clientId);
        if (apiLanguage) {
          setLanguageState(apiLanguage as Language);
          document.documentElement.lang = apiLanguage;
        } else {
          setLanguageState(defaultLanguage);
          document.documentElement.lang = defaultLanguage;
        }
        hasLoadedFromAPIRef.current = true;
      } catch (error) {
        console.error('Erreur chargement langue:', error);
        setLanguageState(defaultLanguage);
      } finally {
        setIsLanguageLoading(false);
        isLoadingRef.current = false;
      }
    };

    syncLanguage();
  }, [enseigneCode, clientId, isClientLoading, defaultLanguage, getClientLanguage]);

  // Réinitialiser quand l'utilisateur change
  useEffect(() => {
    if (!clientId) {
      hasLoadedFromAPIRef.current = false;
      lastLoadTimestampRef.current = 0;
    }
  }, [clientId]);

  const setLanguage = async (lang: Language) => {
    if (lang === language) return;
    
    setLanguageState(lang);
    document.documentElement.lang = lang;

    if (enseigneCode && clientId) {
      setIsLanguageLoading(true);
      try {
        await updateClientLanguage(enseigneCode, clientId, lang);
      } catch (error) {
        console.error('Erreur sauvegarde langue:', error);
      } finally {
        setIsLanguageLoading(false);
      }
    }
  };

  const t = (key: string, params?: Record<string, string | number>): string => {
    if (!isReady) return key;
    const value = getNestedValue(translationsCache[language], key);
    if (value === undefined) return key;
    return replaceParams(value, params);
  };

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage, 
      t,
      isLanguageLoading,
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
